/**
 * Mobile i18n — 镜像 desktop 的 react-intl 方案（扁平 key 的 zh/en JSON）。
 * 桌面版还要从 main 进程 IPC 同步语言；手机端没有 IPC，只用 localStorage +
 * navigator.language 检测。
 */

import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react'
import { IntlProvider, useIntl } from 'react-intl'

import zhMessages from '../locales/zh.json'
import enMessages from '../locales/en.json'

export type Lang = 'zh' | 'en'

const messages: Record<Lang, Record<string, string>> = { zh: zhMessages, en: enMessages }
const STORAGE_KEY = 'cortex-m-lang'

function initialLang(): Lang {
  try {
    const cached = localStorage.getItem(STORAGE_KEY)
    if (cached === 'zh' || cached === 'en') return cached
  } catch {
    // localStorage 不可用 — 退到浏览器语言
  }
  return navigator.language.toLowerCase().startsWith('zh') ? 'zh' : 'en'
}

interface LanguageState {
  lang: Lang
  setLang: (lang: Lang) => void
}

const LanguageContext = createContext<LanguageState | null>(null)

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(initialLang)

  useEffect(() => {
    document.documentElement.lang = lang === 'zh' ? 'zh-CN' : 'en'
  }, [lang])

  const setLang = useCallback((l: Lang) => {
    setLangState(l)
    try {
      localStorage.setItem(STORAGE_KEY, l)
    } catch {}
  }, [])

  return (
    <LanguageContext.Provider value={{ lang, setLang }}>
      <IntlProvider locale={lang} messages={messages[lang]} defaultLocale="en">
        {children}
      </IntlProvider>
    </LanguageContext.Provider>
  )
}

/**
 * Returns { lang, setLang, t }.
 *   t('pair.success')                — 直接查表
 *   t('pair.enterSpace', { title })  — ICU 插值
 */
export function useLang() {
  const ctx = useContext(LanguageContext)
  if (!ctx) throw new Error('useLang must be inside LanguageProvider')
  const intl = useIntl()
  const t = useCallback(
    (id: string, values?: Record<string, string | number>) => intl.formatMessage({ id }, values),
    [intl],
  )
  return { lang: ctx.lang, setLang: ctx.setLang, t }
}
